import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import menpointLogo from "../../assets/menpoint.png";
import {
  submitFeedback,
  type FeedbackPayload,
} from "../../requests/feedback/feedback";
import styles from "./ReviewsPage.module.css";

interface FeedbackFormState {
  fullName: string;
  phone: string;
  city: string;
  visitDate: string;
  customerType: string;
  orderNumber: string;
  purchaseRating: number;
  consultantRating: number;
  foundEverything: string;
  recommendation: string;
  liked: string[];
  comment: string;
  contactPermission: "" | "Да, можно" | "Нет";
  preferredContactMethod: string;
  website: string;
  personalDataConsent: boolean;
}

interface RatingFieldProps {
  legend: string;
  name: "purchaseRating" | "consultantRating";
  value: number;
  leftCaption: string;
  rightCaption: string;
  onChange: (value: number) => void;
}

interface SegmentedFieldProps {
  legend: string;
  name:
    | "customerType"
    | "foundEverything"
    | "recommendation"
    | "contactPermission"
    | "preferredContactMethod";
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

const getLocalDate = () => {
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - timezoneOffset).toISOString().slice(0, 10);
};

const initialFormState = (): FeedbackFormState => ({
  fullName: "",
  phone: "",
  city: "",
  visitDate: getLocalDate(),
  customerType: "",
  orderNumber: "",
  purchaseRating: 0,
  consultantRating: 0,
  foundEverything: "",
  recommendation: "",
  liked: [],
  comment: "",
  contactPermission: "",
  preferredContactMethod: "",
  website: "",
  personalDataConsent: false,
});

const normalizePhone = (value: string) => {
  let digits = value.replace(/\D/g, "");

  if (digits.length === 10) digits = `7${digits}`;
  if (digits.length === 11 && digits.startsWith("8")) {
    digits = `7${digits.slice(1)}`;
  }

  return digits.length === 11 && digits.startsWith("7")
    ? `+${digits}`
    : "";
};

const formatPhone = (value: string) => {
  let digits = value.replace(/\D/g, "");

  if (digits.startsWith("8")) digits = `7${digits.slice(1)}`;
  if (!digits.startsWith("7")) digits = `7${digits}`;
  digits = digits.slice(0, 11);

  const national = digits.slice(1);
  let formatted = "+7";
  if (national.length) formatted += ` (${national.slice(0, 3)}`;
  if (national.length >= 3) formatted += ")";
  if (national.length > 3) formatted += ` ${national.slice(3, 6)}`;
  if (national.length > 6) formatted += `-${national.slice(6, 8)}`;
  if (national.length > 8) formatted += `-${national.slice(8, 10)}`;
  return formatted;
};

const isDetailedCommentRequired = (form: FeedbackFormState) =>
  (form.purchaseRating > 0 && form.purchaseRating <= 3) ||
  (form.consultantRating > 0 && form.consultantRating <= 3) ||
  (Boolean(form.foundEverything) &&
    form.foundEverything !== "Да, полностью") ||
  (Boolean(form.recommendation) && form.recommendation !== "Да");

const likedOptions = [
  "Ассортимент",
  "Качество изделий",
  "Уровень сервиса",
  "Работа консультанта",
  "Атмосфера магазина",
];

const RatingField = ({
  legend,
  name,
  value,
  leftCaption,
  rightCaption,
  onChange,
}: RatingFieldProps) => (
  <fieldset className={styles.fieldset}>
    <legend className={styles.legend}>{legend}</legend>
    <div className={styles.rating} role="radiogroup" aria-label={legend}>
      {[1, 2, 3, 4, 5].map((rating) => {
        const id = `${name}-${rating}`;
        return (
          <span key={id} className={styles.ratingOption}>
            <input
              id={id}
              type="radio"
              name={name}
              value={rating}
              checked={value === rating}
              onChange={() => onChange(rating)}
            />
            <label htmlFor={id} aria-label={`${rating} из 5`}>
              <span aria-hidden="true">{value === rating ? "★" : "☆"}</span>
            </label>
          </span>
        );
      })}
    </div>
    <div className={styles.ratingCaption}>
      <span>{leftCaption}</span>
      <span>{rightCaption}</span>
    </div>
  </fieldset>
);

const SegmentedField = ({
  legend,
  name,
  value,
  options,
  onChange,
}: SegmentedFieldProps) => (
  <fieldset className={styles.fieldset}>
    <legend className={styles.legend}>{legend}</legend>
    <div className={styles.segmented}>
      {options.map((option, index) => {
        const id = `${name}-${index}`;
        return (
          <span key={option} className={styles.segmentedOption}>
            <input
              id={id}
              type="radio"
              name={name}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
            />
            <label htmlFor={id}>{option}</label>
          </span>
        );
      })}
    </div>
  </fieldset>
);

const ReviewsPage = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<FeedbackFormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useRef<HTMLDivElement>(null);
  const resetTimerRef = useRef<number | null>(null);

  const isStoreMode = searchParams.get("mode") === "store";
  const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL?.trim();

  const progress = useMemo(() => {
    const requiredFields = [
      form.fullName.trim().length >= 2,
      Boolean(normalizePhone(form.phone)),
      Boolean(form.visitDate),
      Boolean(form.customerType),
      form.purchaseRating > 0,
      form.consultantRating > 0,
      Boolean(form.foundEverything),
      Boolean(form.recommendation),
      Boolean(form.contactPermission),
      form.personalDataConsent,
    ];

    if (form.contactPermission === "Да, можно") {
      requiredFields.push(Boolean(form.preferredContactMethod));
    }

    return (
      (requiredFields.filter(Boolean).length / requiredFields.length) * 100
    );
  }, [form]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "MENPOINT — обратная связь";
    document.body.classList.remove("main-page", "tasks-page");
    document.body.classList.add("reviews-page");

    return () => {
      document.title = previousTitle;
      document.body.classList.remove("reviews-page");
    };
  }, []);

  useEffect(() => {
    if (!isStoreMode || !isSuccess) {
      return;
    }

    resetTimerRef.current = window.setTimeout(() => {
      setForm(initialFormState());
      setIsSuccess(false);
      setError("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 8000);

    return () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, [isStoreMode, isSuccess]);

  const updateField = <K extends keyof FeedbackFormState,>(
    field: K,
    value: FeedbackFormState[K],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleLiked = (option: string) => {
    setForm((current) => ({
      ...current,
      liked: current.liked.includes(option)
        ? current.liked.filter((item) => item !== option)
        : [...current.liked, option],
    }));
  };

  const resetForm = () => {
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
    }
    setForm(initialFormState());
    setError("");
    setIsSuccess(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showError = (message: string) => {
    setError(message);
    window.requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const validate = () => {
    if (form.fullName.trim().length < 2) {
      return "Укажите имя и фамилию.";
    }
    if (!normalizePhone(form.phone)) {
      return "Укажите корректный номер телефона.";
    }
    if (!form.visitDate) return "Укажите дату визита.";
    if (form.visitDate > getLocalDate()) {
      return "Дата визита не может быть в будущем.";
    }
    if (!form.customerType) {
      return "Укажите, были ли вы в MENPOINT раньше.";
    }
    if (!form.purchaseRating) return "Поставьте оценку покупке.";
    if (!form.consultantRating)
      return "Поставьте оценку работе консультанта.";
    if (!form.foundEverything)
      return "Укажите, удалось ли подобрать всё необходимое.";
    if (!form.recommendation)
      return "Укажите, готовы ли вы рекомендовать MENPOINT.";
    if (isDetailedCommentRequired(form) && !form.comment.trim()) {
      return "Расскажите, пожалуйста, что именно мы можем улучшить.";
    }
    if (!form.contactPermission) {
      return "Укажите, можно ли связаться с вами по этому отзыву.";
    }
    if (
      form.contactPermission === "Да, можно" &&
      !form.preferredContactMethod
    ) {
      return "Выберите удобный способ связи.";
    }
    if (!form.personalDataConsent) {
      return "Подтвердите согласие на обработку персональных данных.";
    }
    return "";
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      showError(validationError);
      return;
    }

    const payload: FeedbackPayload = {
      formVersion: 2,
      channel: isStoreMode
        ? "в магазине"
        : searchParams.get("source") || "с сайта",
      fullName: form.fullName.trim(),
      phone: normalizePhone(form.phone),
      city: form.city.trim(),
      visitDate: form.visitDate,
      customerType: form.customerType,
      orderNumber: form.orderNumber.trim(),
      purchaseRating: form.purchaseRating,
      consultantRating: form.consultantRating,
      foundEverything: form.foundEverything,
      recommendation: form.recommendation,
      liked: form.liked,
      comment: form.comment.trim(),
      contactPermission: form.contactPermission === "Да, можно",
      preferredContactMethod:
        form.contactPermission === "Да, можно"
          ? form.preferredContactMethod
          : "",
      website: form.website,
      personalDataConsent: form.personalDataConsent,
      pageUrl: window.location.href.slice(0, 500),
    };

    setIsSubmitting(true);
    try {
      await submitFeedback(payload);
      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (submitError) {
      showError(
        submitError instanceof Error
          ? submitError.message
          : "Не удалось отправить форму. Попробуйте ещё раз позже.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formTitle = isStoreMode
    ? "Ваше мнение важно для нас"
    : "Спасибо за покупку!";
  const formSubtitle = isStoreMode
    ? "Поделитесь впечатлением о покупке — это займёт около двух минут."
    : "Будем признательны, если вы поделитесь впечатлением о визите в MENPOINT.";

  return (
    <main className={styles.page}>
      <aside className={styles.editorial}>
        <Link className={styles.brandLink} to="/" aria-label="На главную">
          <img className={styles.brandLogo} src={menpointLogo} alt="MENPOINT" />
          <span>выбери свою точку</span>
        </Link>

        <div className={styles.editorialCopy}>
          <p className={styles.eyebrow}>Private client experience</p>
          <h1 className={styles.editorialTitle}>
            {isStoreMode ? "Один визит." : "Ваше мнение"}
            <br />
            <em>{isStoreMode ? "Важное мнение." : "для нас важно"}</em>
          </h1>
          <p className={styles.editorialLead}>
            {isStoreMode
              ? "Короткая форма для планшета в магазине. После отправки она автоматически подготовится для следующего клиента."
              : "Помогите нам становиться лучше. Короткая обратная связь занимает около двух минут."}
          </p>
        </div>

        <div className={styles.facts}>
          <div>
            <strong>2</strong>
            <span>минуты на заполнение</span>
          </div>
          <div>
            <strong>8</strong>
            <span>понятных вопросов</span>
          </div>
          <div>
            <strong>∞</strong>
            <span>внимание к деталям</span>
          </div>
        </div>
      </aside>

      <section className={styles.formSide}>
        <article className={styles.card}>
          <div className={styles.progress} aria-hidden="true">
            <div
              className={styles.progressBar}
              style={{ width: `${isSuccess ? 100 : progress}%` }}
            />
          </div>

          {isSuccess ? (
            <section className={styles.success} aria-live="polite">
              <div>
                <img
                  className={`${styles.formLogo} ${styles.formLogoDark}`}
                  src={menpointLogo}
                  alt="MENPOINT"
                />
                <div className={styles.successIcon} aria-hidden="true">
                  ✓
                </div>
                <h2>Спасибо!</h2>
                <p>
                  {isStoreMode
                    ? "Ваш ответ отправлен. Форма скоро будет готова для следующего клиента."
                    : "Ваш ответ отправлен. Он поможет нам улучшать сервис MENPOINT."}
                </p>
                {!isStoreMode && (
                  <button
                    className={styles.resetButton}
                    type="button"
                    onClick={resetForm}
                  >
                    Заполнить ещё раз
                  </button>
                )}
              </div>
            </section>
          ) : (
            <div className={styles.cardInner}>
              <Link className={styles.mobileBrand} to="/">
                <img
                  className={`${styles.formLogo} ${styles.formLogoDark}`}
                  src={menpointLogo}
                  alt="MENPOINT"
                />
                <span>На главную</span>
              </Link>

              <header className={styles.intro}>
                <img
                  className={`${styles.formLogo} ${styles.desktopFormLogo} ${styles.formLogoDark}`}
                  src={menpointLogo}
                  alt="MENPOINT"
                />
                <h2>{formTitle}</h2>
                <p>{formSubtitle}</p>
                <span className={styles.timeBadge}>◷ Около 2 минут</span>
              </header>

              <form className={styles.form} noValidate onSubmit={handleSubmit}>
                <section className={styles.formSection}>
                  <div className={styles.sectionHeading}>
                    <div>
                      <span>Сначала немного о визите</span>
                      <p>
                        Это поможет найти покупку и передать отзыв нужному
                        менеджеру.
                      </p>
                    </div>
                    <small>* обязательные поля</small>
                  </div>

                  <div className={styles.fieldGrid}>
                    <label className={styles.fieldLabel}>
                      <span>Имя и фамилия *</span>
                      <input
                        className={styles.textInput}
                        type="text"
                        name="fullName"
                        required
                        maxLength={120}
                        autoComplete="name"
                        placeholder="Например, Иван Петров"
                        value={form.fullName}
                        onChange={(event) =>
                          updateField("fullName", event.target.value)
                        }
                      />
                    </label>

                    <label className={styles.fieldLabel}>
                      <span>Номер телефона *</span>
                      <input
                        className={styles.textInput}
                        type="text"
                        name="phone"
                        required
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="+7 (999) 123-45-67"
                        value={form.phone}
                        onChange={(event) =>
                          updateField("phone", event.target.value.slice(0, 18))
                        }
                        onBlur={() => {
                          const normalizedPhone = normalizePhone(form.phone);
                          if (normalizedPhone) {
                            updateField("phone", formatPhone(normalizedPhone));
                          }
                        }}
                      />
                      <small className={styles.fieldHint}>
                        Нужен для идентификации покупки. Свяжемся только с
                        разрешения ниже.
                      </small>
                    </label>

                    <label className={styles.fieldLabel}>
                      <span>Дата визита *</span>
                      <input
                        className={styles.textInput}
                        type="date"
                        name="visitDate"
                        required
                        max={getLocalDate()}
                        value={form.visitDate}
                        onChange={(event) =>
                          updateField("visitDate", event.target.value)
                        }
                      />
                    </label>

                    <label className={styles.fieldLabel}>
                      <span>
                        Город <small>— по желанию</small>
                      </span>
                      <input
                        className={styles.textInput}
                        type="text"
                        name="city"
                        maxLength={120}
                        autoComplete="address-level2"
                        placeholder="Например, Москва"
                        value={form.city}
                        onChange={(event) =>
                          updateField("city", event.target.value)
                        }
                      />
                    </label>
                  </div>

                  <SegmentedField
                    legend="Вы впервые в MENPOINT? *"
                    name="customerType"
                    value={form.customerType}
                    options={["Первый визит", "Повторный визит"]}
                    onChange={(value) => updateField("customerType", value)}
                  />

                  <label className={styles.fieldLabel}>
                    <span>
                      Номер чека или заказа <small>— по желанию</small>
                    </span>
                    <input
                      className={styles.textInput}
                      type="text"
                      name="orderNumber"
                      maxLength={80}
                      placeholder="Поможет быстрее найти вашу покупку"
                      value={form.orderNumber}
                      onChange={(event) =>
                        updateField("orderNumber", event.target.value)
                      }
                    />
                  </label>
                </section>

                <label className={styles.honeypot} aria-hidden="true">
                  Ваш сайт
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(event) =>
                      updateField("website", event.target.value)
                    }
                  />
                </label>

                <RatingField
                  legend="1. Насколько вы довольны покупкой?"
                  name="purchaseRating"
                  value={form.purchaseRating}
                  leftCaption="Не доволен"
                  rightCaption="Полностью доволен"
                  onChange={(value) => updateField("purchaseRating", value)}
                />

                <RatingField
                  legend="2. Как вы оцените работу консультанта?"
                  name="consultantRating"
                  value={form.consultantRating}
                  leftCaption="Можно лучше"
                  rightCaption="Отлично"
                  onChange={(value) => updateField("consultantRating", value)}
                />

                <SegmentedField
                  legend="3. Удалось ли подобрать всё, что вы искали?"
                  name="foundEverything"
                  value={form.foundEverything}
                  options={["Да, полностью", "Частично", "Нет"]}
                  onChange={(value) => updateField("foundEverything", value)}
                />

                <SegmentedField
                  legend="4. Порекомендовали бы вы MENPOINT знакомым?"
                  name="recommendation"
                  value={form.recommendation}
                  options={["Да", "Возможно", "Нет"]}
                  onChange={(value) => updateField("recommendation", value)}
                />

                <fieldset className={styles.fieldset}>
                  <legend className={styles.legend}>
                    5. Что вам понравилось больше всего?{" "}
                    <small>Можно выбрать несколько</small>
                  </legend>
                  <div className={styles.chips}>
                    {likedOptions.map((option, index) => {
                      const id = `liked-${index}`;
                      return (
                        <span key={option} className={styles.chipOption}>
                          <input
                            id={id}
                            type="checkbox"
                            name="liked"
                            value={option}
                            checked={form.liked.includes(option)}
                            onChange={() => toggleLiked(option)}
                          />
                          <label htmlFor={id}>{option}</label>
                        </span>
                      );
                    })}
                  </div>
                </fieldset>

                <label className={styles.fieldLabel}>
                  <span>
                    Комментарий или пожелание{" "}
                    {isDetailedCommentRequired(form) ? (
                      <small>— обязательно для этого ответа</small>
                    ) : (
                      <small>— по желанию</small>
                    )}
                  </span>
                  <textarea
                    className={styles.textarea}
                    name="comment"
                    maxLength={1500}
                    placeholder="Расскажите, что особенно понравилось или что мы можем улучшить"
                    value={form.comment}
                    onChange={(event) =>
                      updateField("comment", event.target.value)
                    }
                  />
                </label>

                <section className={styles.contactCard}>
                  <SegmentedField
                    legend="Можно ли связаться с вами по этому отзыву? *"
                    name="contactPermission"
                    value={form.contactPermission}
                    options={["Да, можно", "Нет"]}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        contactPermission: value as "Да, можно" | "Нет",
                        preferredContactMethod:
                          value === "Да, можно"
                            ? current.preferredContactMethod
                            : "",
                      }))
                    }
                  />

                  {form.contactPermission === "Да, можно" && (
                    <SegmentedField
                      legend="Как удобнее связаться? *"
                      name="preferredContactMethod"
                      value={form.preferredContactMethod}
                      options={["Звонок", "WhatsApp", "Telegram"]}
                      onChange={(value) =>
                        updateField("preferredContactMethod", value)
                      }
                    />
                  )}
                </section>

                <label className={styles.consent}>
                  <input
                    type="checkbox"
                    required
                    checked={form.personalDataConsent}
                    onChange={(event) =>
                      updateField(
                        "personalDataConsent",
                        event.target.checked,
                      )
                    }
                  />
                  <span>
                    Я согласен(на) на обработку имени, телефона и других
                    указанных данных
                    {privacyPolicyUrl ? (
                      <>
                        {" "}в соответствии с{" "}
                        <a
                          href={privacyPolicyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          политикой конфиденциальности
                        </a>
                      </>
                    ) : (
                      " исключительно для обработки этого отзыва"
                    )}
                    . *
                  </span>
                </label>

                {error && (
                  <div
                    ref={errorRef}
                    className={styles.formError}
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                <button
                  className={styles.submitButton}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Отправляем…" : "Отправить отзыв"}
                </button>
                <p className={styles.finePrint}>
                  Разрешение связаться относится только к этому отзыву и не
                  является согласием на рекламные рассылки.
                </p>
              </form>
            </div>
          )}
        </article>
      </section>
    </main>
  );
};

export default ReviewsPage;
